import React, { useState, useEffect } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Zap,
  Table,
  ChevronRight,
  ChevronDown,
  Database,
  Play,
  Truck,
  Target,
  Navigation,
  Code,
  Boxes,
  FileText,
  Info,
  X
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { biTableStatusCollection } from '../services/biRecords';

export default function PowerBIWorkflow({ tenantId } = {}) {
  // Step states (now 8 steps total including Complete)
  const [currentStep, setCurrentStep] = useState(1);

  // Rabi Invoice upload state
  const [rabiFile, setRabiFile] = useState(null);
  const [rabiUploading, setRabiUploading] = useState(false);
  const [rabiProgress, setRabiProgress] = useState(0);
  const [rabiResult, setRabiResult] = useState(null);
  const [rabiError, setRabiError] = useState(null);

  // Production upload state
  const [prodFile, setProdFile] = useState(null);
  const [prodUploading, setProdUploading] = useState(false);
  const [prodProgress, setProdProgress] = useState(0);
  const [prodResult, setProdResult] = useState(null);
  const [prodError, setProdError] = useState(null);

  // Inventory upload state (Step 2.1)
  const [invFile, setInvFile] = useState(null);
  const [invUploading, setInvUploading] = useState(false);
  const [invProgress, setInvProgress] = useState(0);
  const [invResult, setInvResult] = useState(null);
  const [invError, setInvError] = useState(null);

  // HTS upload state (Step 2.2)
  const [htsFile, setHtsFile] = useState(null);
  const [htsUploading, setHtsUploading] = useState(false);
  const [htsProgress, setHtsProgress] = useState(0);
  const [htsResult, setHtsResult] = useState(null);
  const [htsError, setHtsError] = useState(null);

  // BL sync state
  const [blSyncing, setBlSyncing] = useState(false);
  const [blResult, setBlResult] = useState(null);
  const [blError, setBlError] = useState(null);
  const [blConfig, setBlConfig] = useState(null);

  // LAP sync state
  const [lapSyncing, setLapSyncing] = useState(false);
  const [lapResult, setLapResult] = useState(null);
  const [lapError, setLapError] = useState(null);
  const [lapConfig, setLapConfig] = useState(null);

  // Container Log sync state
  const [containerSyncing, setContainerSyncing] = useState(false);
  const [containerResult, setContainerResult] = useState(null);
  const [containerError, setContainerError] = useState(null);
  const [containerConfig, setContainerConfig] = useState(null);

  // Walmart status state
  const [walmartLoading, setWalmartLoading] = useState(false);
  const [walmartStatus, setWalmartStatus] = useState(null);
  const [walmartError, setWalmartError] = useState(null);

  // Target status state
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [targetError, setTargetError] = useState(null);

  // Transform state
  const [transforming, setTransforming] = useState(false);
  const [transformResult, setTransformResult] = useState(null);
  const [transformError, setTransformError] = useState(null);
  const [showSQL, setShowSQL] = useState(false);

  // Run All (Steps 3-7) state
  const [runningAll, setRunningAll] = useState(false);
  const [runAllStep, setRunAllStep] = useState(null);
  const [runAllError, setRunAllError] = useState(null);

  // Table info popup state
  const [tableStatuses, setTableStatuses] = useState({});
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);

  const db = getFirestore();

  // Load BL and LAP configs on mount
  useEffect(() => {
    loadRoutingConfigs();
  }, []);

  // CHA-616 — statuses are per tenant, so reload when the tenant is known or changes.
  useEffect(() => {
    // CHA-616 — status is tenant data: drop the previous tenant's statuses immediately, and let only a
    // response for the STILL-CURRENT tenant commit (a slow tenant-A read must not land after tenant B).
    setTableStatuses({});
    if (!tenantId) return undefined;
    let current = true;
    loadTableStatuses(tenantId, () => current);
    return () => { current = false; };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load table statuses from Firestore
  const loadTableStatuses = async (forTenantId, isCurrent) => {
    try {
      // CHA-616 — the tenant's own status records (CHA-87 E2a), not the shared root collection.
      const statusRef = biTableStatusCollection(db, forTenantId);
      const snapshot = await getDocs(statusRef);
      if (!isCurrent()) return; // the tenant changed while this read was in flight
      const statuses = {};
      snapshot.forEach(doc => {
        statuses[doc.id] = { id: doc.id, ...doc.data() };
      });
      setTableStatuses(statuses);
    } catch (error) {
      console.error('Error loading table statuses:', error);
    }
  };

  const loadRoutingConfigs = async () => {
    try {
      const configsRef = collection(db, 'routing_sync_configs');
      const snapshot = await getDocs(configsRef);

      snapshot.forEach(doc => {
        const config = { id: doc.id, ...doc.data() };
        if (config.tableName === 'bl' && config.enabled) {
          setBlConfig(config);
        } else if (config.tableName === 'lap' && config.enabled) {
          setLapConfig(config);
        } else if (config.tableName === 'container_log' && config.enabled) {
          setContainerConfig(config);
        }
      });
    } catch (error) {
      console.error('Error loading routing configs:', error);
    }
  };

  // Handle file selections
  const handleRabiFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setRabiFile(file);
      setRabiResult(null);
      setRabiError(null);
    }
  };

  const handleProdFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProdFile(file);
      setProdResult(null);
      setProdError(null);
    }
  };

  const handleInvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setInvFile(file);
      setInvResult(null);
      setInvError(null);
    }
  };

  const handleHtsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setHtsFile(file);
      setHtsResult(null);
      setHtsError(null);
    }
  };

  // Upload Rabi Invoice
  const uploadRabiInvoice = async () => {
    if (!rabiFile) return;

    setRabiUploading(true);
    setRabiProgress(0);
    setRabiError(null);

    try {
      const storage = getStorage();
      // Upload to inmocean-raw-data path to avoid triggering processFileToBigQuery
      // Then processBIUpload will handle everything correctly like BI4
      const storagePath = `inmocean-raw-data/powerbi-workflow/rabi_invoices_v2.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, rabiFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setRabiProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setRabiProgress(55);
      const functions = getFunctions();
      // Use processBIUpload exactly like BI4
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setRabiProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'rabi_invoices_v2',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform dates from Excel serial numbers to proper DATE format
      setRabiProgress(80);
      const transformRabiInvoicesTable = httpsCallable(functions, 'transformRabiInvoicesTable', { timeout: 330000 });
      await transformRabiInvoicesTable({
        sourceTable: 'rabi_invoices_v2',
        targetTable: 'rabi_invoices_v2',
        dataset: 'inmocean_raw'
      });

      setRabiProgress(100);
      setRabiResult({
        success: true,
        filename: rabiFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setRabiUploading(false);
      setCurrentStep(2);
    } catch (err) {
      setRabiError(err.message);
      setRabiUploading(false);
    }
  };

  // Upload Production Data (multi-tab file: ERRORS, OpenWIP-DUE, 2025Rrcpts)
  const uploadProductionData = async () => {
    if (!prodFile) return;

    setProdUploading(true);
    setProdProgress(0);
    setProdError(null);

    try {
      const storage = getStorage();
      // Upload to path that triggers processFileToBigQuery with multi-tab support
      // Path format: inmocean-raw-data/{fileType}/{filename} - fileType must match schema key
      const storagePath = `inmocean-raw-data/production_cost_sheets/${Date.now()}_${prodFile.name}`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, prodFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 80);
            setProdProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      // Storage trigger (processFileToBigQuery) will handle multi-tab processing
      // Tabs: ERRORS → production_errors, OpenWIP-DUE → production_open_wip, 2025Rrcpts → production_2025_receipts
      setProdProgress(90);

      // Wait a moment for the storage trigger to start processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      setProdProgress(100);
      setProdResult({
        success: true,
        filename: prodFile.name,
        rowCount: 0, // Will be populated by storage trigger
        message: 'File uploaded - processing tabs: ERRORS, OpenWIP-DUE, 2025Rrcpts'
      });
      setProdUploading(false);
      setCurrentStep(2.1);
    } catch (err) {
      setProdError(err.message);
      setProdUploading(false);
    }
  };

  // Upload Inventory Data (Step 2.1)
  const uploadInventoryData = async () => {
    if (!invFile) return;

    setInvUploading(true);
    setInvProgress(0);
    setInvError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow/inventory.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, invFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setInvProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setInvProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setInvProgress(50);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'inventory',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform inventory table (Pascal_Case, DivisionName, Company, numeric fields)
      setInvProgress(70);
      const transformInventoryTable = httpsCallable(functions, 'transformInventoryTable', { timeout: 330000 });
      await transformInventoryTable({
        sourceTable: 'inventory',
        targetTable: 'inventory',
        dataset: 'inmocean_raw'
      });
      console.log('✅ Inventory table transformed');

      // Create error_ench table after inventory upload
      setInvProgress(90);
      const createErrorEnch = httpsCallable(functions, 'createErrorEnchTable', { timeout: 150000 });
      try {
        await createErrorEnch();
        console.log('✅ error_ench table created');
      } catch (errEnch) {
        console.warn('⚠️ Could not create error_ench table:', errEnch.message);
      }

      setInvProgress(100);
      setInvResult({
        success: true,
        filename: invFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setInvUploading(false);
      setCurrentStep(2.2);
    } catch (err) {
      setInvError(err.message);
      setInvUploading(false);
    }
  };

  // Upload HTS Data (Step 2.2)
  const uploadHtsData = async () => {
    if (!htsFile) return;

    setHtsUploading(true);
    setHtsProgress(0);
    setHtsError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow/hts_updates.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, htsFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setHtsProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setHtsProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setHtsProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'hts_updates',
        datasetName: 'inmocean_raw',
        tenantId
      });

      setHtsProgress(100);
      setHtsResult({
        success: true,
        filename: htsFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setHtsUploading(false);
      setCurrentStep(3);
    } catch (err) {
      setHtsError(err.message);
      setHtsUploading(false);
    }
  };

  // Sync BL from Google Sheets
  const syncBL = async () => {
    if (!blConfig) {
      setBlError('BL sync not configured. Contact admin.');
      return;
    }

    setBlSyncing(true);
    setBlError(null);

    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });

      const result = await syncFunction({
        sheetId: blConfig.sheetId,
        tabName: blConfig.tabName,
        tableName: blConfig.tableName,
        configId: blConfig.id
      });

      if (result.data?.success) {
        setBlResult({
          success: true,
          rowCount: result.data.rowsSynced || 0
        });
        setCurrentStep(4);
      } else {
        throw new Error(result.data?.error || 'BL sync failed');
      }
    } catch (err) {
      setBlError(err.message);
    } finally {
      setBlSyncing(false);
    }
  };

  // Sync LAP from Google Sheets
  const syncLAP = async () => {
    if (!lapConfig) {
      setLapError('LAP sync not configured. Contact admin.');
      return;
    }

    setLapSyncing(true);
    setLapError(null);

    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });

      const result = await syncFunction({
        sheetId: lapConfig.sheetId,
        tabName: lapConfig.tabName,
        tableName: lapConfig.tableName,
        configId: lapConfig.id
      });

      if (result.data?.success) {
        setLapResult({
          success: true,
          rowCount: result.data.rowsSynced || 0
        });
        setCurrentStep(5);
      } else {
        throw new Error(result.data?.error || 'LAP sync failed');
      }
    } catch (err) {
      setLapError(err.message);
    } finally {
      setLapSyncing(false);
    }
  };

  // Sync Container Log from Google Sheets
  const syncContainerLog = async () => {
    if (!containerConfig) {
      setContainerError('Container Log sync not configured. Contact admin.');
      return;
    }

    setContainerSyncing(true);
    setContainerError(null);

    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });

      const result = await syncFunction({
        sheetId: containerConfig.sheetId,
        tabName: containerConfig.tabName,
        tableName: containerConfig.tableName,
        configId: containerConfig.id
      });

      if (result.data?.success) {
        setContainerResult({
          success: true,
          rowCount: result.data.rowsSynced || 0
        });
      } else {
        throw new Error(result.data?.error || 'Container Log sync failed');
      }
    } catch (err) {
      setContainerError(err.message);
    } finally {
      setContainerSyncing(false);
    }
  };

  // Check Walmart tables status
  const checkWalmartStatus = async () => {
    setWalmartLoading(true);
    setWalmartError(null);

    try {
      const functions = getFunctions();
      const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });

      const result = await checkExternalTables({
        tables: [
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_bwi_csv' },
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_dsdc_csv' },
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_img_csv' }
        ]
      });

      setWalmartStatus(result.data);
      setCurrentStep(6);
    } catch (err) {
      setWalmartError(err.message);
    } finally {
      setWalmartLoading(false);
    }
  };

  // Check Target table status
  const checkTargetStatus = async () => {
    setTargetLoading(true);
    setTargetError(null);

    try {
      const functions = getFunctions();
      const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });

      const result = await checkExternalTables({
        tables: [
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'target_csv' }
        ]
      });

      setTargetStatus(result.data);
      setCurrentStep(7);
    } catch (err) {
      setTargetError(err.message);
    } finally {
      setTargetLoading(false);
    }
  };

  // Run Transform
  const runTransform = async () => {
    setTransforming(true);
    setTransformResult(null);
    setTransformError(null);

    try {
      const functions = getFunctions();
      const runOrderFileTransform = httpsCallable(functions, 'runOrderFileTransform', { timeout: 570000 });

      const result = await runOrderFileTransform();
      setTransformResult(result.data);
      setCurrentStep(8);
    } catch (err) {
      setTransformError(err.message);
    } finally {
      setTransforming(false);
    }
  };

  // Reset workflow
  const resetWorkflow = () => {
    setCurrentStep(1);
    setRabiFile(null);
    setRabiResult(null);
    setRabiError(null);
    setProdFile(null);
    setProdResult(null);
    setProdError(null);
    setInvFile(null);
    setInvResult(null);
    setInvError(null);
    setHtsFile(null);
    setHtsResult(null);
    setHtsError(null);
    setBlResult(null);
    setBlError(null);
    setLapResult(null);
    setLapError(null);
    setContainerResult(null);
    setContainerError(null);
    setWalmartStatus(null);
    setWalmartError(null);
    setTargetStatus(null);
    setTargetError(null);
    setTransformResult(null);
    setTransformError(null);
    setRunAllError(null);
  };

  // Run All Steps 3-7 in sequence
  const runAllSteps = async () => {
    setRunningAll(true);
    setRunAllError(null);

    try {
      const functions = getFunctions();

      // Step 3: BL Sync
      if (blConfig) {
        setRunAllStep('BL Sync (Step 3)');
        setBlSyncing(true);
        setBlError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({
            sheetId: blConfig.sheetId,
            tabName: blConfig.tabName,
            tableName: blConfig.tableName,
            configId: blConfig.id
          });
          if (result.data?.success) {
            setBlResult({ success: true, rowCount: result.data.rowsSynced || 0 });
          }
        } catch (err) {
          setBlError(err.message);
        } finally {
          setBlSyncing(false);
        }
      }

      // Step 4: LAP Sync
      if (lapConfig) {
        setRunAllStep('LAP Sync (Step 4)');
        setLapSyncing(true);
        setLapError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({
            sheetId: lapConfig.sheetId,
            tabName: lapConfig.tabName,
            tableName: lapConfig.tableName,
            configId: lapConfig.id
          });
          if (result.data?.success) {
            setLapResult({ success: true, rowCount: result.data.rowsSynced || 0 });
          }
        } catch (err) {
          setLapError(err.message);
        } finally {
          setLapSyncing(false);
        }
      }

      // Container Log Sync
      if (containerConfig) {
        setRunAllStep('Container Log Sync');
        setContainerSyncing(true);
        setContainerError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({
            sheetId: containerConfig.sheetId,
            tabName: containerConfig.tabName,
            tableName: containerConfig.tableName,
            configId: containerConfig.id
          });
          if (result.data?.success) {
            setContainerResult({ success: true, rowCount: result.data.rowsSynced || 0 });
          }
        } catch (err) {
          setContainerError(err.message);
        } finally {
          setContainerSyncing(false);
        }
      }

      // Step 5: Walmart Status
      setRunAllStep('Walmart Check (Step 5)');
      setWalmartLoading(true);
      setWalmartError(null);
      try {
        const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
        const result = await checkExternalTables({
          tables: [
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_bwi_csv' },
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_dsdc_csv' },
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_img_csv' }
          ]
        });
        setWalmartStatus(result.data);
      } catch (err) {
        setWalmartError(err.message);
      } finally {
        setWalmartLoading(false);
      }

      // Step 6: Target Status
      setRunAllStep('Target Check (Step 6)');
      setTargetLoading(true);
      setTargetError(null);
      try {
        const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
        const result = await checkExternalTables({
          tables: [
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'target_csv' }
          ]
        });
        setTargetStatus(result.data);
      } catch (err) {
        setTargetError(err.message);
      } finally {
        setTargetLoading(false);
      }

      // Step 7: Transform
      setRunAllStep('Transform (Step 7)');
      setTransforming(true);
      setTransformResult(null);
      setTransformError(null);
      try {
        const runOrderFileTransform = httpsCallable(functions, 'runOrderFileTransform', { timeout: 570000 });
        const result = await runOrderFileTransform();
        setTransformResult(result.data);
        setCurrentStep(8);
      } catch (err) {
        setTransformError(err.message);
      } finally {
        setTransforming(false);
      }

      setRunAllStep('Complete!');
    } catch (err) {
      setRunAllError(err.message);
    } finally {
      setRunningAll(false);
      setRunAllStep(null);
    }
  };

  const StepIndicator = ({ step, title, isActive, isCompleted }) => (
    <div className={`flex flex-col items-center gap-1 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        isCompleted ? 'bg-green-500 text-white' :
        isActive ? 'bg-purple-600 text-white' :
        'bg-gray-200 text-gray-600'
      }`}>
        {isCompleted ? <CheckCircle className="w-4 h-4" /> : step}
      </div>
      <span className={`text-xs font-medium text-center ${isActive ? 'text-purple-700' : 'text-gray-500'}`}>{title}</span>
    </div>
  );

  const StepCard = ({ stepNum, title, subtitle, icon: Icon, isActive, isCompleted, children, iconColor = 'purple' }) => (
    <div className={`bg-white rounded-lg border ${isActive ? 'border-purple-300 ring-2 ring-purple-100' : 'border-gray-200'} p-5`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${isActive ? `bg-${iconColor}-100` : 'bg-gray-100'}`}>
          <Icon className={`h-5 w-5 ${isActive ? `text-${iconColor}-600` : 'text-gray-500'}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">Step {stepNum}: {title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        {isCompleted && <CheckCircle className="h-5 w-5 text-green-500" />}
      </div>
      {children}
    </div>
  );

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Info popup component
  const InfoPopup = ({ tableName, sheetUrl, onClose }) => {
    const status = tableStatuses[tableName];
    return (
      <div className="absolute z-50 top-0 right-0 mt-8 mr-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs">
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-800">Table Info</span>
          <button onClick={onClose} className="p-0.5 hover:bg-gray-100 rounded">
            <X className="w-3 h-3 text-gray-500" />
          </button>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <span className="text-gray-500">Table:</span>
            <span className="font-mono text-gray-800">{tableName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Dataset:</span>
            <span className="font-mono text-gray-800">inmocean_raw</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Last Updated:</span>
            <span className="text-gray-800">{status ? formatTimestamp(status.last_updated) : 'Never'}</span>
          </div>
          {status?.row_count !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Rows:</span>
              <span className="text-gray-800">{status.row_count?.toLocaleString()}</span>
            </div>
          )}
          {status?.source_file && (
            <div className="flex justify-between">
              <span className="text-gray-500">Source:</span>
              <span className="text-gray-800 truncate max-w-32" title={status.source_file}>{status.source_file}</span>
            </div>
          )}
          {sheetUrl && (
            <div className="pt-1 border-t border-gray-100">
              <a href={sheetUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> Open Google Sheet
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact card for grid layout
  const CompactCard = ({ stepNum, title, icon: Icon, isActive, isCompleted, children, borderColor = 'gray', tableName, sheetUrl }) => (
    <div className={`bg-white rounded-lg border-2 ${
      isCompleted ? 'border-green-300 bg-green-50' :
      isActive ? `border-${borderColor}-400 ring-2 ring-${borderColor}-100` :
      'border-gray-200'
    } p-3 h-full relative`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
          isCompleted ? 'bg-green-500 text-white' :
          isActive ? 'bg-purple-600 text-white' :
          'bg-gray-200 text-gray-600'
        }`}>
          {isCompleted ? <CheckCircle className="w-3 h-3" /> : stepNum}
        </div>
        <Icon className={`h-4 w-4 ${isCompleted ? 'text-green-600' : isActive ? 'text-purple-600' : 'text-gray-400'}`} />
        <span className="text-sm font-medium text-gray-800 truncate flex-1">{title}</span>
        {tableName && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveInfoPopup(activeInfoPopup === tableName ? null : tableName);
            }}
            className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="View table info"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {activeInfoPopup === tableName && (
        <InfoPopup tableName={tableName} sheetUrl={sheetUrl} onClose={() => setActiveInfoPopup(null)} />
      )}
      <div className="text-xs">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Zap className="h-7 w-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Power BI Update Workflow</h1>
              <p className="text-gray-600 text-sm">Follow the steps to update your Power BI data</p>
            </div>
          </div>
          <button
            onClick={runAllSteps}
            disabled={runningAll || (!blConfig && !lapConfig)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md"
          >
            {runningAll ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                {runAllStep || 'Running...'}
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run All (3-7)
              </>
            )}
          </button>
        </div>
        {runAllError && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
            <XCircle className="h-4 w-4" />
            {runAllError}
          </div>
        )}
      </div>

      {/* Progress Steps - Grouped */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
        <div className="flex items-center gap-4 min-w-max">
          {/* ERP Data Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-blue-600 mb-2 uppercase tracking-wide">ERP Data</span>
            <div className="flex items-center gap-1 bg-blue-50 rounded-lg px-2 py-1 border border-blue-200">
              <StepIndicator step={1} title="Orders" isActive={currentStep === 1} isCompleted={!!rabiResult} />
              <ChevronRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
              <StepIndicator step={2} title="Prod" isActive={currentStep === 2} isCompleted={!!prodResult} />
              <ChevronRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
              <StepIndicator step="2.1" title="Inv" isActive={currentStep === 2.1} isCompleted={!!invResult} />
              <ChevronRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
              <StepIndicator step="2.2" title="HTS" isActive={currentStep === 2.2} isCompleted={!!htsResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Warehouse Data Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-green-600 mb-2 uppercase tracking-wide">Warehouse</span>
            <div className="flex items-center gap-1 bg-green-50 rounded-lg px-2 py-1 border border-green-200">
              <StepIndicator step={3} title="BL" isActive={currentStep === 3} isCompleted={!!blResult} />
              <ChevronRight className="w-3 h-3 text-green-300 flex-shrink-0" />
              <StepIndicator step={4} title="LAP" isActive={currentStep === 4} isCompleted={!!lapResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Customer TMS Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-orange-600 mb-2 uppercase tracking-wide">Customer TMS</span>
            <div className="flex items-center gap-1 bg-orange-50 rounded-lg px-2 py-1 border border-orange-200">
              <StepIndicator step={5} title="Walmart" isActive={currentStep === 5} isCompleted={!!walmartStatus} />
              <ChevronRight className="w-3 h-3 text-orange-300 flex-shrink-0" />
              <StepIndicator step={6} title="Target" isActive={currentStep === 6} isCompleted={!!targetStatus} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Transform Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-purple-600 mb-2 uppercase tracking-wide">Transform</span>
            <div className="flex items-center gap-1 bg-purple-50 rounded-lg px-2 py-1 border border-purple-200">
              <StepIndicator step={7} title="Run" isActive={currentStep === 7} isCompleted={!!transformResult} />
              <ChevronRight className="w-3 h-3 text-purple-300 flex-shrink-0" />
              <StepIndicator step={8} title="Done" isActive={currentStep === 8} isCompleted={false} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== ERP DATA SECTION ========== */}
      <div className="flex items-center gap-3 mt-2">
        <div className="h-px flex-1 bg-blue-200"></div>
        <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-2">
          <Database className="h-4 w-4" /> ERP Data
        </span>
        <div className="h-px flex-1 bg-blue-200"></div>
      </div>

      {/* ERP Data Row - 4 cards */}
      <div className="grid grid-cols-4 gap-3">
        {/* Step 1: Orders */}
        <CompactCard stepNum={1} title="Orders" icon={FileSpreadsheet} isActive={currentStep === 1} isCompleted={!!rabiResult} borderColor="blue" tableName="rabi_invoices_v2">
          {!rabiResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls" onChange={handleRabiFileSelect} className="hidden" id="rabi-file-input" />
              <label htmlFor="rabi-file-input" className="flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3 w-3" /> {rabiFile ? rabiFile.name.substring(0, 12) + '...' : 'Select File'}
              </label>
              {rabiFile && (
                <button onClick={uploadRabiInvoice} disabled={rabiUploading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {rabiUploading ? <><RefreshCw className="h-3 w-3 animate-spin" /> {rabiProgress}%</> : <><Play className="h-3 w-3" /> Upload</>}
                </button>
              )}
              {rabiError && <div className="text-red-500 truncate">{rabiError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{rabiResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Step 2: Production */}
        <CompactCard stepNum={2} title="Production" icon={Database} isActive={currentStep === 2} isCompleted={!!prodResult} borderColor="blue" tableName="production_cost_sheets">
          {!prodResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls" onChange={handleProdFileSelect} className="hidden" id="prod-file-input" />
              <label htmlFor="prod-file-input" className="flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3 w-3" /> {prodFile ? prodFile.name.substring(0, 12) + '...' : 'Select File'}
              </label>
              {prodFile && (
                <button onClick={uploadProductionData} disabled={prodUploading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {prodUploading ? <><RefreshCw className="h-3 w-3 animate-spin" /> {prodProgress}%</> : <><Play className="h-3 w-3" /> Upload</>}
                </button>
              )}
              {prodError && <div className="text-red-500 truncate">{prodError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{prodResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Step 2.1: Inventory */}
        <CompactCard stepNum="2.1" title="Inventory" icon={Boxes} isActive={currentStep === 2.1} isCompleted={!!invResult} borderColor="blue" tableName="inventory">
          {!invResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleInvFileSelect} className="hidden" id="inv-file-input" />
              <label htmlFor="inv-file-input" className="flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3 w-3" /> {invFile ? invFile.name.substring(0, 12) + '...' : 'Select File'}
              </label>
              {invFile && (
                <button onClick={uploadInventoryData} disabled={invUploading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {invUploading ? <><RefreshCw className="h-3 w-3 animate-spin" /> {invProgress}%</> : <><Play className="h-3 w-3" /> Upload</>}
                </button>
              )}
              {invError && <div className="text-red-500 truncate">{invError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{invResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Step 2.2: HTS */}
        <CompactCard stepNum="2.2" title="HTS Data" icon={FileText} isActive={currentStep === 2.2} isCompleted={!!htsResult} borderColor="blue" tableName="hts_updates">
          {!htsResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleHtsFileSelect} className="hidden" id="hts-file-input" />
              <label htmlFor="hts-file-input" className="flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3 w-3" /> {htsFile ? htsFile.name.substring(0, 12) + '...' : 'Select File'}
              </label>
              {htsFile && (
                <button onClick={uploadHtsData} disabled={htsUploading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {htsUploading ? <><RefreshCw className="h-3 w-3 animate-spin" /> {htsProgress}%</> : <><Play className="h-3 w-3" /> Upload</>}
                </button>
              )}
              {htsError && <div className="text-red-500 truncate">{htsError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{htsResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>
      </div>

      {/* ========== WAREHOUSE DATA SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-green-200"></div>
        <span className="text-sm font-semibold text-green-600 uppercase tracking-wide flex items-center gap-2">
          <Boxes className="h-4 w-4" /> Warehouse Data
        </span>
        <div className="h-px flex-1 bg-green-200"></div>
      </div>

      {/* Warehouse Row - 2 cards (expandable) */}
      <div className="grid grid-cols-4 gap-3">
        {/* Step 3: BL */}
        <CompactCard stepNum={3} title="BL Routing" icon={Navigation} isActive={currentStep === 3} isCompleted={!!blResult} borderColor="green" tableName="bl" sheetUrl={blConfig?.spreadsheetId ? `https://docs.google.com/spreadsheets/d/${blConfig.spreadsheetId}` : null}>
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z"/>
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z"/>
              <path fill="#4285f4" d="M13 3v6h6l-6-6z"/>
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!blResult ? (
            <div className="space-y-2">
              {blConfig ? (
                <button onClick={syncBL} disabled={blSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {blSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {blError && <div className="text-red-500 truncate">{blError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{blResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Step 4: LAP */}
        <CompactCard stepNum={4} title="LAP Routing" icon={Navigation} isActive={currentStep === 4} isCompleted={!!lapResult} borderColor="green" tableName="lap" sheetUrl={lapConfig?.spreadsheetId ? `https://docs.google.com/spreadsheets/d/${lapConfig.spreadsheetId}` : null}>
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z"/>
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z"/>
              <path fill="#4285f4" d="M13 3v6h6l-6-6z"/>
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!lapResult ? (
            <div className="space-y-2">
              {lapConfig ? (
                <button onClick={syncLAP} disabled={lapSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {lapSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {lapError && <div className="text-red-500 truncate">{lapError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{lapResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Container Log */}
        <CompactCard stepNum="" title="Container Log" icon={Boxes} isActive={false} isCompleted={!!containerResult} borderColor="green" tableName="container_log" sheetUrl={containerConfig?.sheetId ? `https://docs.google.com/spreadsheets/d/${containerConfig.sheetId}` : null}>
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z"/>
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z"/>
              <path fill="#4285f4" d="M13 3v6h6l-6-6z"/>
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!containerResult ? (
            <div className="space-y-2">
              {containerConfig ? (
                <button onClick={syncContainerLog} disabled={containerSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {containerSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {containerError && <div className="text-red-500 truncate">{containerError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{containerResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        {/* Placeholder for future warehouses */}
        <div className="border-2 border-dashed border-green-200 rounded-lg p-3 flex items-center justify-center text-green-400 text-xs">
          + More
        </div>
      </div>

      {/* ========== CUSTOMER TMS SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-orange-200"></div>
        <span className="text-sm font-semibold text-orange-600 uppercase tracking-wide flex items-center gap-2">
          <Truck className="h-4 w-4" /> Customer TMS
        </span>
        <div className="h-px flex-1 bg-orange-200"></div>
      </div>

      {/* Customer TMS Row - 2 cards (expandable) */}
      <div className="grid grid-cols-4 gap-3">
        {/* Step 5: Walmart */}
        <div className={`bg-white rounded-lg border-2 ${
          walmartStatus ? 'border-green-300 bg-green-50' :
          currentStep === 5 ? 'border-blue-400 ring-2 ring-blue-100' :
          'border-gray-200'
        } p-3 h-full relative`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              walmartStatus ? 'bg-green-500 text-white' :
              currentStep === 5 ? 'bg-purple-600 text-white' :
              'bg-gray-200 text-gray-600'
            }`}>
              {walmartStatus ? <CheckCircle className="w-3 h-3" /> : 5}
            </div>
            <svg viewBox="0 0 32 32" className="h-5 w-5">
              <g fill="#ffc220">
                <path d="M16 3 L17.5 11 L16 14 L14.5 11 Z"/>
                <path d="M16 29 L17.5 21 L16 18 L14.5 21 Z"/>
                <path d="M3 16 L11 14.5 L14 16 L11 17.5 Z"/>
                <path d="M29 16 L21 14.5 L18 16 L21 17.5 Z"/>
                <path d="M6.5 6.5 L12 10.5 L13.5 13.5 L10.5 12 Z"/>
                <path d="M25.5 25.5 L20 21.5 L18.5 18.5 L21.5 20 Z"/>
                <path d="M25.5 6.5 L21.5 12 L18.5 13.5 L20 10.5 Z"/>
                <path d="M6.5 25.5 L10.5 20 L13.5 18.5 L12 21.5 Z"/>
              </g>
            </svg>
            <span className="text-sm font-bold text-[#0071ce] flex-1">Walmart</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveInfoPopup(activeInfoPopup === 'walmart' ? null : 'walmart');
              }}
              className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
              title="View table info"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          {activeInfoPopup === 'walmart' && (
            <div className="absolute z-50 top-0 right-0 mt-8 mr-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-gray-800">Walmart Tables</span>
                <button onClick={() => setActiveInfoPopup(null)} className="p-0.5 hover:bg-gray-100 rounded">
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="text-gray-500 mb-1">BigQuery External Tables:</div>
                <div className="font-mono text-gray-800 text-[10px] space-y-0.5">
                  <div>• actualizer2.inmocean_raw.walmart_tms_bwi_csv</div>
                  <div>• actualizer2.inmocean_raw.walmart_tms_dsdc_csv</div>
                  <div>• actualizer2.inmocean_raw.walmart_tms_img_csv</div>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs">
            <div className="flex items-center gap-1 mb-1">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#6b7280" strokeWidth="2">
                <ellipse cx="12" cy="6" rx="8" ry="3"/>
                <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6"/>
                <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6"/>
              </svg>
              <span className="text-[10px] text-gray-400">Database</span>
            </div>
            {!walmartStatus ? (
              <div className="space-y-2">
                <div className="text-gray-500 truncate">3 TMS tables</div>
                <button onClick={checkWalmartStatus} disabled={walmartLoading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-[#0071ce] text-white rounded hover:bg-[#004c91] disabled:opacity-50">
                  {walmartLoading ? <><RefreshCw className="h-3 w-3 animate-spin" /></> : <><Database className="h-3 w-3" /> Check</>}
                </button>
                {walmartError && <div className="text-red-500 truncate">{walmartError}</div>}
              </div>
            ) : (
              <div className="text-green-600">
                {walmartStatus.tables?.reduce((sum, t) => sum + (t.rowCount || 0), 0).toLocaleString()} rows
              </div>
            )}
          </div>
        </div>

        {/* Step 6: Target */}
        <div className={`bg-white rounded-lg border-2 ${
          targetStatus ? 'border-green-300 bg-green-50' :
          currentStep === 6 ? 'border-red-400 ring-2 ring-red-100' :
          'border-gray-200'
        } p-3 h-full relative`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              targetStatus ? 'bg-green-500 text-white' :
              currentStep === 6 ? 'bg-purple-600 text-white' :
              'bg-gray-200 text-gray-600'
            }`}>
              {targetStatus ? <CheckCircle className="w-3 h-3" /> : 6}
            </div>
            <svg viewBox="0 0 1615 1551" className="h-5 w-5">
              <circle cx="808" cy="775" r="775" fill="#cc0000"/>
              <circle cx="808" cy="775" r="530" fill="#fff"/>
              <circle cx="808" cy="775" r="285" fill="#cc0000"/>
            </svg>
            <span className="text-sm font-medium text-gray-800 flex-1">Target</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveInfoPopup(activeInfoPopup === 'target' ? null : 'target');
              }}
              className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
              title="View table info"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          {activeInfoPopup === 'target' && (
            <div className="absolute z-50 top-0 right-0 mt-8 mr-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-gray-800">Target Table</span>
                <button onClick={() => setActiveInfoPopup(null)} className="p-0.5 hover:bg-gray-100 rounded">
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="text-gray-500 mb-1">BigQuery External Table:</div>
                <div className="font-mono text-gray-800 text-[10px]">
                  <div>• actualizer2.inmocean_raw.target_csv</div>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs">
            <div className="flex items-center gap-1 mb-1">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#6b7280" strokeWidth="2">
                <ellipse cx="12" cy="6" rx="8" ry="3"/>
                <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6"/>
                <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6"/>
              </svg>
              <span className="text-[10px] text-gray-400">Database</span>
            </div>
            {!targetStatus ? (
              <div className="space-y-2">
                <div className="text-gray-500 truncate">Target TMS</div>
                <button onClick={checkTargetStatus} disabled={targetLoading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-[#cc0000] text-white rounded hover:bg-[#990000] disabled:opacity-50">
                  {targetLoading ? <><RefreshCw className="h-3 w-3 animate-spin" /></> : <><Database className="h-3 w-3" /> Check</>}
                </button>
                {targetError && <div className="text-red-500 truncate">{targetError}</div>}
              </div>
            ) : (
              <div className="text-green-600">{targetStatus.tables?.[0]?.rowCount?.toLocaleString()} rows</div>
            )}
          </div>
        </div>

        {/* Placeholder for future customers */}
        <div className="border-2 border-dashed border-orange-200 rounded-lg p-3 flex items-center justify-center text-orange-400 text-xs">
          + More
        </div>
        <div className="border-2 border-dashed border-orange-200 rounded-lg p-3 flex items-center justify-center text-orange-400 text-xs">
          + More
        </div>
      </div>

      {/* ========== DATA TRANSFORMATION SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-purple-200"></div>
        <span className="text-sm font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-2">
          <Zap className="h-4 w-4" /> Data Transformation
        </span>
        <div className="h-px flex-1 bg-purple-200"></div>
      </div>

      {/* Transform Row */}
      <div className="grid grid-cols-4 gap-3">
        {/* Step 7: Transform */}
        <div className={`bg-white rounded-lg border-2 ${
          transformResult ? 'border-green-300 bg-green-50' :
          currentStep === 7 ? 'border-purple-400 ring-2 ring-purple-100' :
          'border-gray-200'
        } p-3 h-full`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              transformResult ? 'bg-green-500 text-white' :
              currentStep === 7 ? 'bg-purple-600 text-white' :
              'bg-gray-200 text-gray-600'
            }`}>
              {transformResult ? <CheckCircle className="w-3 h-3" /> : 7}
            </div>
            <Zap className={`h-4 w-4 ${transformResult ? 'text-green-600' : 'text-purple-600'}`} />
            <span className="text-sm font-medium text-gray-800">Transform</span>
          </div>
          <div className="text-xs">
            <div className="flex items-center gap-1 mb-1">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#9333ea" strokeWidth="2">
                <rect x="4" y="2" rx="2" width="16" height="20"/>
                <line x1="8" y1="6" x2="16" y2="6"/>
                <line x1="8" y1="10" x2="16" y2="10"/>
                <line x1="8" y1="14" x2="12" y2="14"/>
                <line x1="8" y1="18" x2="16" y2="18"/>
              </svg>
              <span className="text-[10px] text-gray-400">SQL Transform</span>
            </div>
            {!transformResult ? (
              <div className="space-y-2">
                <button onClick={runTransform} disabled={transforming} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
                  {transforming ? <><RefreshCw className="h-3 w-3 animate-spin" /> Running...</> : <><Zap className="h-3 w-3" /> Run</>}
                </button>
                {transformError && <div className="text-red-500 truncate">{transformError}</div>}
              </div>
            ) : (
              <div className="text-green-600">
                {(transformResult.results?.orderfile?.rows || 0) +
                 (transformResult.results?.production_open_wip?.rows || 0) +
                 (transformResult.results?.production_receipts?.rows || 0)} rows
              </div>
            )}
          </div>
        </div>

        {/* Step 8: Done */}
        <div className={`bg-white rounded-lg border-2 ${
          currentStep === 8 ? 'border-green-400 bg-green-50 ring-2 ring-green-100' :
          'border-gray-200'
        } p-3 h-full`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              currentStep === 8 ? 'bg-green-500 text-white' :
              'bg-gray-200 text-gray-600'
            }`}>
              {currentStep === 8 ? <CheckCircle className="w-3 h-3" /> : 8}
            </div>
            <CheckCircle className={`h-4 w-4 ${currentStep === 8 ? 'text-green-600' : 'text-gray-400'}`} />
            <span className="text-sm font-medium text-gray-800">Done</span>
          </div>
          <div className="text-xs">
            {currentStep === 8 ? (
              <div className="space-y-2">
                <div className="text-green-600 font-medium">Complete!</div>
                <button onClick={resetWorkflow} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700">
                  <RefreshCw className="h-3 w-3" /> Reset
                </button>
              </div>
            ) : (
              <div className="text-gray-400">Waiting...</div>
            )}
          </div>
        </div>

        {/* Show SQL button */}
        <div className="border-2 border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center">
          <button
            onClick={() => setShowSQL(!showSQL)}
            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
          >
            <Code className="h-4 w-4" />
            {showSQL ? 'Hide SQL' : 'View SQL'}
          </button>
        </div>

        {/* Empty placeholder */}
        <div className="border-2 border-dashed border-purple-200 rounded-lg p-3 flex items-center justify-center text-purple-400 text-xs">

        </div>
      </div>

      {/* SQL Viewer (collapsible) */}
      {showSQL && (
        <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono overflow-x-auto max-h-[400px] overflow-y-auto">
          <pre className="whitespace-pre-wrap">{`-- Step 1/5: chainto_inmocean_orderfile
DROP TABLE IF EXISTS \`actualizer2.inmocean_raw.chainto_inmocean_orderfile_csv\`;

CREATE TABLE \`actualizer2.inmocean_raw.chainto_inmocean_orderfile_csv\` AS
WITH base AS (
  SELECT
    a.*,
    o.OrderStatus AS Status,
    o.PTNumber,
    o.RouteDate,
    o.ConformationNumber,
    o.AppoitmenetDate,
    o.ShipDate,
    o.Notes,
    o.IMGNotes,

    COALESCE(
      SAFE.PARSE_DATE('%Y-%m-%d', LEFT(CAST(a.Start AS STRING), 10)),
      SAFE.PARSE_DATE('%m/%d/%Y', CAST(a.Start AS STRING)),
      DATE(SAFE_CAST(a.Start AS TIMESTAMP)),
      DATE(SAFE_CAST(a.Start AS DATETIME))
    ) AS StartDate,

    COALESCE(
      SAFE.PARSE_DATE('%Y-%m-%d', LEFT(CAST(a.Ending AS STRING), 10)),
      SAFE.PARSE_DATE('%m/%d/%Y', CAST(a.Ending AS STRING)),
      DATE(SAFE_CAST(a.Ending AS TIMESTAMP)),
      DATE(SAFE_CAST(a.Ending AS DATETIME))
    ) AS EndDate,

    COALESCE(
      SAFE.PARSE_DATE('%Y-%m-%d', LEFT(CAST(a.Invoice_Date AS STRING), 10)),
      SAFE.PARSE_DATE('%m/%d/%Y', CAST(a.Invoice_Date AS STRING)),
      DATE(SAFE_CAST(a.Invoice_Date AS TIMESTAMP)),
      DATE(SAFE_CAST(a.Invoice_Date AS DATETIME))
    ) AS InvoiceDate

  FROM \`actualizer2.inmocean_raw.rabi_invoices_2\` a
  LEFT JOIN (
    -- Deduplicate oo1: take one row per PT
    SELECT * FROM (
      SELECT *, ROW_NUMBER() OVER (PARTITION BY PT ORDER BY ShipDate DESC, AppoitmenetDate DESC) as rn
      FROM \`rabi-354003.db_data.oo1\`
    ) WHERE rn = 1
  ) o
    ON CAST(a.Pick_Number AS STRING) = o.PT
)

SELECT
  base.*,

  IF(base.Sts = 'I', 'Invoiced',
    IF(base.Sts = 'O', 'Open',
      IF(
        CAST(base.Pick_Number AS STRING) <> "",
        IF(
          IFNULL(CAST(base.RouteDate AS STRING), "") <> "",
          IF(
            CAST(base.RouteDate AS STRING) = "nan",
            "2.Not Routed",
            IF(
              CAST(base.RouteDate AS STRING) = "Not in WM System",
              "2.Not Routed",
              IF(
                CAST(base.ConformationNumber AS STRING) = "nan",
                "3.Routed",
                IF(
                  CAST(base.ConformationNumber AS STRING) = "0",
                  "3.Routed",
                  IF(
                    IFNULL(CAST(base.ConformationNumber AS STRING), "") <> "",
                    IF(IFNULL(CAST(base.AppoitmenetDate AS STRING), "") <> "", "5.Scheduled", "4.Confirmed"),
                    "3.Routed"
                  )
                )
              )
            )
          ),
          "2.Not Routed"
        ),
        "1.No Routing Data"
      )
    )
  ) AS OrderStatus2,

  CASE base.Sts
    WHEN 'I' THEN IFNULL(base.InvoiceDate, DATE '1900-01-01')
    WHEN 'O' THEN IFNULL(base.StartDate,   DATE '1900-01-01')
    WHEN 'P' THEN COALESCE(
                  SAFE.PARSE_DATE('%Y-%m-%d', LEFT(CAST(base.ShipDate AS STRING), 10)),
                  SAFE.PARSE_DATE('%m/%d/%Y', CAST(base.ShipDate AS STRING)),
                  DATE(SAFE_CAST(base.ShipDate AS TIMESTAMP)),
                  DATE(SAFE_CAST(base.ShipDate AS DATETIME)),
                  DATE '1900-01-01'
                )
    ELSE NULL
  END AS ProjectedDate,

  CASE CAST(base.Invoice_Date AS STRING)
    WHEN '/  /' THEN ''
    WHEN '/ /'  THEN ''
    ELSE LEFT(CAST(base.Invoice_Date AS STRING), 10)
  END AS InvoiceDateAsDate,

  DATE_DIFF(base.StartDate, CURRENT_DATE(), DAY) AS Start_days_difference,
  DATE_DIFF(base.EndDate,   CURRENT_DATE(), DAY) AS End_days_difference,

  CASE
    WHEN base.EndDate IS NULL OR base.StartDate IS NULL THEN '0.Invalid Dates'
    WHEN DATE_DIFF(CURRENT_DATE(), base.EndDate, DAY) > 0 THEN '3.Pass Cancel'
    WHEN DATE_DIFF(CURRENT_DATE(), base.StartDate, DAY) > 0 THEN '2.In Window'
    ELSE '1.Pre Start'
  END AS Ship_window_status,

  CASE
    WHEN CAST(base.MABD AS STRING) IN ('/ /','/  /','') THEN NULL
    ELSE FORMAT_TIMESTAMP('%Y-%m-%d', SAFE_CAST(base.MABD AS TIMESTAMP))
  END AS FormatedMABD,

  CASE
    WHEN CAST(base.Order_Date AS STRING) IN ('/ /','/  /','') THEN NULL
    ELSE FORMAT_TIMESTAMP('%Y-%m-%d', SAFE_CAST(base.Order_Date AS TIMESTAMP))
  END AS Formated_OrderDate,

  CASE
    WHEN CAST(base.Entry_Date AS STRING) IN ('/ /','/  /','') THEN NULL
    ELSE FORMAT_TIMESTAMP('%Y-%m-%d', SAFE_CAST(base.Entry_Date AS TIMESTAMP))
  END AS Formated_EntryDate,

  IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Ext_GP AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0) AS Net_Gross_Profit,
  IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Gross_Price_2 AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0) AS Gross_Price_,
  IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Ext_Trx_Std_Cost AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0) AS Cost_Wac,
  IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Royalty_net_price AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0) AS Royality_Number,

  IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Ext_GP AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0)
  - IFNULL(SAFE_CAST(REGEXP_REPLACE(CAST(base.Royalty_net_price AS STRING), r'[^0-9.\\-]', '') AS NUMERIC), 0) AS Net_GP,

  CASE
    WHEN base.Division IN ('30','35','38') THEN '9.BWI(30/35/38)'
    WHEN base.Division = '16' THEN '8.Innovation(16)'
    WHEN base.Division = '40' THEN 'Meta(40)'
    WHEN base.Division = '42' THEN '7.IMG Apparel(42)'
    WHEN base.Division = '55' THEN '7.Regent(55)'
    WHEN base.Division = '41' THEN '6.Lands End(41)'
    WHEN base.Division = '60' THEN '4.Anne Cole(60)'
    WHEN base.Division = '61' THEN '5.CeCe(61)'
    WHEN base.Division IN ('77','78') THEN '3.Hurley(77/78)'
    WHEN base.Division = '15' THEN '2.Accessories(15)'
    WHEN base.Division = '10' THEN '1.InMocean'
    ELSE '1.InMocean'
  END AS DivisionName,

  CASE
    WHEN base.InvoiceDate IS NOT NULL
     AND base.InvoiceDate >= DATE_TRUNC(CURRENT_DATE(), MONTH)
     AND base.InvoiceDate <= CURRENT_DATE()
    THEN 1 ELSE 0
  END AS Invoice_MTD_Flag,

  CASE
    WHEN base.Division IN ('30','35','38') THEN 'BWI'
    WHEN base.Division = '16' THEN 'INNOVATION'
    WHEN base.Division = '40' THEN 'META'
    WHEN base.Division = '42' THEN 'IMG APPAREL'
    ELSE 'IMG'
  END AS Company
FROM base;`}</pre>
        </div>
      )}
    </div>
  );
}
